import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import MembersEmptyIllustration from '@ui/assets/illustrations/members_empty.svg'
import PipelinesEmptyIllustration from '@ui/assets/illustrations/pipelines_empty.svg'
import { Button, Modal } from '@ui/components/core'
import { MenuItem } from '@ui/components/elements/TableList/TableList'
import { ChevronLeftIcon } from '@ui/components/icons'
import { Member, Pipeline } from '@ui/interfaces'
import { InviteTeamMemberForm } from '@ui/pages/settings/components/forms/InviteTeamMemberForm'
import { useSettings } from '@ui/providers/SettingsProvider'
import { teamService } from '@ui/services'
import clsx from 'clsx'

import { Members } from './Members'
import { Pipelines } from './Pipelines'

enum TeamDetailsTab {
  Pipelines = 'pipelines',
  Members = 'members',
}

export const TeamDetails = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentMember, selectedTeam, setSelectedTeam, orgMembers } = useSettings()

  const [tab, setTab] = useState<TeamDetailsTab>(TeamDetailsTab.Members)

  const [isInviteTeamMemberModalOpen, setIsInviteTeamMemberModalOpen] = useState(false)
  const [teamMembers, setTeamMembers] = useState<Member[]>([])
  const [pipelines] = useState<Pipeline[]>([])

  const {
    data: team,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['team', selectedTeam!.id],
    queryFn: () => teamService.getTeam(selectedTeam!.id, currentMember as Member),
  })

  const { mutateAsync: removeTeamMember } = useMutation({
    mutationFn: (id: string) => teamService.removeTeamMember(id, currentMember as Member),
    onSuccess: () => {
      teamService.getTeam(selectedTeam!.id, currentMember as Member).then((team) => {
        const members = orgMembers.filter((member) =>
          team.memberships.some((membership) => membership.user_id === member.id)
        )
        setTeamMembers(members)
      })
    },
  })

  useEffect(() => {
    // We have to map the current orgMembers to the teamMemberships ids
    // since the data lives in two seperate dbs

    // Get all the orgMembers that are in the team
    const orgMemberships = orgMembers.filter(
      (member) => team?.memberships.some((membership) => membership.user_id === member.id)
    )

    // We want to delete on the membership id, not the user id
    const members = orgMemberships.map((member) => ({
      id: team?.memberships.find((membership) => membership.user_id === member.id)?.id,
      email: member.email,
      role: member.role,
    }))

    setTeamMembers(members as Member[])
  }, [orgMembers, team])

  const isOnlyMember = useMemo(() => {
    return teamMembers.length === 1 && teamMembers[0].email === currentMember.email
  }, [teamMembers, currentMember])

  const renderTab = () => {
    switch (tab) {
      case TeamDetailsTab.Members:
        // If the team has no members, or the only member is the current user,
        // we don't want to show the members page
        if (teamMembers.length === 0 || isOnlyMember)
          return (
            <div className="size-full flex flex-col items-center justify-start space-y-4">
              <h2 className="text-lg font-medium">No members found</h2>
              <p className="text-sm text-gray-500">Nobody has been invited to this team yet.</p>
              <img src={MembersEmptyIllustration} alt="Members empty illustration" className="size-48" />
            </div>
          )
        return (
          <Members
            members={teamMembers}
            loading={isLoading}
            menuItems={menuItems}
            currentMember={currentMember}
            isOnlyMember={isOnlyMember}
          />
        )
      case TeamDetailsTab.Pipelines:
        if (pipelines.length === 0)
          return (
            <div className="size-full flex flex-col items-center justify-start space-y-4">
              <h2 className="text-lg font-medium">No pipelines found</h2>
              <p className="text-sm text-gray-500">No pipelines have been assigned to this team yet.</p>
              <img src={PipelinesEmptyIllustration} alt="Pipelines empty illustration" className="size-48" />
            </div>
          )
        return <Pipelines pipelines={pipelines} />
    }
  }

  const menuItems: MenuItem[] = [
    {
      label: 'Remove',
      onClick: (id: string) => removeTeamMember(id),
    },
  ]

  return (
    <div className="size-full space-y-6">
      {/* Header */}
      <div className="flex w-full flex-row justify-between">
        <div className="flex flex-row items-center space-x-2">
          <div
            className="flex cursor-pointer items-center justify-center rounded-md bg-transparent p-1 transition-all duration-300 ease-in-out hover:bg-gray-200"
            onClick={() => {
              if (searchParams.get('teamId')) setSearchParams({})
              setSelectedTeam(null)
            }}>
            <ChevronLeftIcon className="size-6 fill-none stroke-gray-900" strokeWidth={1.5} />
          </div>

          <h1 className="text-xl font-bold">{selectedTeam?.name}</h1>
        </div>

        <Button variant="primary-red" size="small" onClick={() => setIsInviteTeamMemberModalOpen(true)}>
          Invite Members
        </Button>
      </div>
      {/* Tabs */}
      <div className="w-full border-b border-gray-200">
        <div className="flex flex-row">
          <div
            className={clsx(
              'flex translate-y-[0.1rem] cursor-pointer flex-row items-center space-x-2 border-b-2  px-4',
              {
                'border-gray-500 text-gray-500': tab !== TeamDetailsTab.Members,
                'border-primary-500 text-primary-500': tab === TeamDetailsTab.Members,
              }
            )}
            onClick={() => setTab(TeamDetailsTab.Members)}>
            <h1 className="text-sm font-medium">Members</h1>
          </div>
          <div
            className={clsx(
              'flex translate-y-[0.1rem] cursor-pointer flex-row items-center space-x-2 border-b-2  px-4',
              {
                'border-gray-500 text-gray-500': tab !== TeamDetailsTab.Pipelines,
                'border-primary-500 text-primary-500': tab === TeamDetailsTab.Pipelines,
              }
            )}
            onClick={() => setTab(TeamDetailsTab.Pipelines)}>
            <h1 className="text-sm font-medium">Pipelines</h1>
          </div>
        </div>
      </div>
      <Modal
        title="Invite members to team"
        isOpen={isInviteTeamMemberModalOpen}
        onClose={() => setIsInviteTeamMemberModalOpen(false)}>
        <InviteTeamMemberForm teamMembers={teamMembers} refetch={refetch} removeTeamMember={removeTeamMember} />
      </Modal>
      {/* Content */}
      {renderTab()}
    </div>
  )
}
